import React from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { Home, ChevronRight, Camera } from 'lucide-react-native';
import { useTheme, SPACING, CONTENT_MAX_WIDTH, FONT_SIZES, TYPOGRAPHY } from '../../theme';
import { DotLabel, ReadinessMeter } from '@components/common';
import { Structure, StructureClaim } from '../../types';

interface PropertyListRowProps {
  structure: Structure;
  claim: StructureClaim;
  onPress: () => void;
  /** "Owner" for a claim, "Asked to evaluate" for an insurer-link pending
   *  property — see PropertyPickerSheet. Omitted on my-properties, where
   *  every row is already known to be a claim. */
  roleLabel?: 'Owner' | 'Asked to evaluate';
  testID: string;
}

/**
 * One property row: Home icon, address, city/state/zip, the shared
 * ReadinessMeter, and a posts-count meta line — extracted verbatim from
 * my-properties.tsx's renderProperty so PropertyPickerSheet can reuse the
 * exact same grammar. `roleLabel` is the only addition, a DotLabel dropped
 * into the meta row (client ask, Sept 2026: "next to property says my
 * role").
 */
export function PropertyListRow({
  structure,
  claim,
  onPress,
  roleLabel,
  testID,
}: PropertyListRowProps) {
  const { colors } = useTheme();
  const completed = claim.progressSnapshot?.completed ?? 0;
  const total = claim.progressSnapshot?.total ?? 0;

  return (
    <View style={styles.itemConstraint}>
      <Pressable
        testID={testID}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={`${structure.address ?? 'Property'}, view details`}
        style={[styles.propertyCard, { borderBottomColor: colors.border }]}>
        <View style={styles.headerRow}>
          <Home size={20} color={colors.textSecondary} style={styles.propertyIcon} />
          <View style={styles.propertyInfo}>
            <Text style={[styles.propertyAddress, { color: colors.text }]} numberOfLines={1}>
              {structure.address || 'Unknown Address'}
            </Text>
            <Text
              style={[styles.propertyLocation, { color: colors.textSecondary }]}
              numberOfLines={1}>
              {structure.city}, {structure.state}
              {structure.zip ? ` ${structure.zip}` : ''}
            </Text>
          </View>
          <ChevronRight size={20} color={colors.textTertiary} />
        </View>

        <ReadinessMeter
          completedCount={completed}
          totalCount={total}
          style={styles.propertyMeter}
        />

        <View style={styles.metaRow}>
          <Camera size={14} color={colors.textTertiary} />
          <Text style={[styles.metaText, { color: colors.textSecondary }]}>
            {structure.postsCount} {structure.postsCount === 1 ? 'Post' : 'Posts'}
          </Text>
          {roleLabel && (
            <DotLabel
              label={roleLabel}
              variant={roleLabel === 'Owner' ? 'progress' : 'primary'}
              style={styles.roleLabel}
            />
          )}
        </View>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  itemConstraint: {
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    alignSelf: 'center',
    paddingHorizontal: SPACING.md,
  },
  propertyCard: {
    width: '100%',
    alignSelf: 'stretch',
    paddingVertical: SPACING.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  propertyIcon: {
    marginRight: SPACING.smd,
  },
  propertyMeter: {
    marginTop: SPACING.smd,
  },
  propertyInfo: {
    flex: 1,
  },
  propertyAddress: {
    ...TYPOGRAPHY.h4,
    fontSize: FONT_SIZES.md,
  },
  propertyLocation: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 2,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: SPACING.smd,
    gap: SPACING.xs,
  },
  metaText: {
    ...TYPOGRAPHY.label,
  },
  roleLabel: {
    marginLeft: 'auto',
  },
});
